import { AsyncPipe, NgStyle } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
  OnChanges,
  OnInit,
  Signal
} from '@angular/core';
import { MatIcon } from '@angular/material/icon';
import { RouterLink } from '@angular/router';
import { NgbTooltip } from '@ng-bootstrap/ng-bootstrap';
import { CarItem } from '@shared/models/car-item';
import { PricingService } from '@shared/services/pricing.service';
import { ComparisonStoreService } from '@shared/stores/comparison-store.service';
import { LazyLoadImageModule } from 'ng-lazyload-image';
import { map, Observable, take } from 'rxjs';

@Component({
  selector: 'evland-car-row',
  templateUrl: './car-row.component.html',
  styleUrls: [ './car-row.component.scss' ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    AsyncPipe,
    NgStyle,
    RouterLink,
    MatIcon,
    NgbTooltip,
    LazyLoadImageModule,
  ],
})
export class CarRowComponent implements OnInit, OnChanges {
  car = input.required<CarItem>();
  isLast = input<boolean>(false);
  isInComparison$: Observable<boolean>;
  formattedPrice: string;
  formattedPriceChange: string;
  priceChangeTooltip: string;
  isPriceChanged: boolean;
  isPriceIncreased: boolean;
  routerLink: Signal<string[]>;

  #comparisonStore = inject(ComparisonStoreService);
  #pricingService = inject(PricingService);

  constructor() {
    this.routerLink = computed(() => {
      const car = this.car();

      return car ? [ (car.isNew ? '/catalog' : '/offers'), car.id ] : null;
    });
  }

  ngOnInit(): void {
    this.isInComparison$ = this.#comparisonStore.list$
      .pipe(
        map((ids: string[]) => !this.car().modelIds.some((i: string) => !ids.some((id: string) => i === id))),
      );
  }

  ngOnChanges() {
    const car = this.car();

    this.formattedPrice = null;

    if (!car.price) {
      return;
    }

    const formattedPrice = this.#pricingService.formatPrice(car.price);

    this.formattedPrice = [
      ...(car.hasBiggerPrice ? [ $localize`від` ] : []),
      formattedPrice
    ].join(' ');

    if (!car.initialPriceDiff?.usd) {
      this.isPriceChanged = false;

      return;
    }

    this.isPriceChanged = true;
    this.isPriceIncreased = car.initialPriceDiff.usd > 0;
    this.formattedPriceChange = this.#pricingService.formatPrice(car.initialPriceDiff.usd * (this.isPriceIncreased ? 1 : -1));
    this.priceChangeTooltip = this.isPriceIncreased ? $localize`Ціна зросла на ${this.formattedPriceChange}` : $localize`Ціна знизилася на ${this.formattedPriceChange}`;
  }

  onCompareClick(event: Event): void {
    event.preventDefault();
    event.stopPropagation();

    this.isInComparison$
      .pipe(
        take(1),
      )
      .subscribe((isInComparison: boolean) => {
        const { modelIds } = this.car();

        isInComparison ? this.#comparisonStore.deleteIds(modelIds) : this.#comparisonStore.addIds(modelIds);
      });
  }
}
